import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchLastContract, fetchSpecialistByID, fetchSpecialistBySlug } from './thunk';
import { ISpecialist } from '../specialists/types';
import { updateArray } from 'src/utils/updateArray';
import { updateReview } from '../specialistReviews/thunk';

type LoadedSpecialistsStore = {
  data: ISpecialist[];
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
};

interface UpdatePayload {
  field: keyof ISpecialist;
  value: any;
  data: Partial<ISpecialist>;
}

export const initialState: LoadedSpecialistsStore = {
  data: [],
  loading: 'idle',
};

const loadedSpecialists = createSlice({
  name: 'loadedSpecialists',
  initialState,
  reducers: {
    updateLoadedSpecialistBy: (state, action: PayloadAction<UpdatePayload>) => {
      const { field, value, data } = action.payload;
      state.data = updateArray<ISpecialist>(
        state.data,
        (specialist) => specialist[field] === value, // Condition to find the specialist
        data // Data to update
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSpecialistByID.pending, (state) => {
        state.loading = 'pending';
      })
      .addCase(fetchSpecialistByID.fulfilled, (state, action) => {
        const isExist = state.data.some((item) => item.id === action.payload.id);

        if (!isExist) {
          state.data.push(action.payload);
        } else {
          state.data = state.data.map((item) => {
            if (item.id === action.payload.id) {
              return {
                ...item,
                ...action.payload,
              };
            }
            return item;
          });
        }

        state.loading = 'succeeded';
      })
      .addCase(fetchLastContract.fulfilled, (state, action) => {
        if (!state.data || !action.payload) return;
        state.data = state.data?.map((item) => {
          if (item.id === action.payload.specialist_id) {
            return {
              ...item,
              client: {
                specialist_client_contract_status: action.payload.status,
                specialist_client_contract_id: action.payload.id,
              },
            };
          }
          return item;
        });
      })
      .addCase(fetchSpecialistByID.rejected, (state) => {
        state.loading = 'failed';
      })
      .addCase(fetchSpecialistBySlug.pending, (state) => {
        state.loading = 'pending';
      })
      .addCase(fetchSpecialistBySlug.fulfilled, (state, action) => {
        const isExist = state.data.some((item) => item.slug === action.payload.slug);
        if (!isExist) {
          state.data.push(action.payload);
        }

        state.loading = 'succeeded';
      })
      .addCase(fetchSpecialistBySlug.rejected, (state) => {
        state.loading = 'failed';
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.data = state.data.map((item) => {
          if (item.id === action.payload.specialist_id) {
            return {
              ...item,
              reviews_count: action.payload.specialist.reviews_count,
              reviews_average_rating: action.payload.specialist.reviews_average_rating,
              reviews_count_by_stars: action.payload.specialist.reviews_count_by_stars,
            };
          }
          return item;
        });
      });
  },
});

export const { updateLoadedSpecialistBy } = loadedSpecialists.actions;

export default loadedSpecialists.reducer;
